import { castString, roundValue } from '@salescore/buff-common'
import {
  convertRecordNodeToSheetRow,
  CORE_CONSTANT,
  type NodePath,
  type SheetRow,
  type ViewQueryRecordNode,
} from '@salescore/core'
import { Array } from 'effect'

import { extractActualAndGoal } from '../../../rsheet/components/body/cell/kpi/extractKpiInfo'
import type { DashboardCsvColumn } from './generateDashboardCsvData'

interface DashboardDimensionSourceColumn {
  title: string
  fieldName: string
  nodePath: NodePath
}

interface DashboardKpiSourceColumn {
  title: string
  fieldName: string
  nodePath: NodePath
  blockLabels: string[]
}

export const generateDashboardCsvColumns = (
  rows: ViewQueryRecordNode[],
  dimensionColumns: DashboardDimensionSourceColumn[],
  kpiColumns: DashboardKpiSourceColumn[],
): DashboardCsvColumn[] => {
  const sheetRows = rows.map((row) =>
    convertRecordNodeToSheetRow(
      row,
      [...dimensionColumns, ...kpiColumns].map((column) => ({
        fieldName: column.fieldName,
        nodePath: column.nodePath,
      })),
    ),
  )

  return [
    ...dimensionColumns.map((column, columnIndex): DashboardCsvColumn => ({
      type: 'dimension',
      title: column.title.replace(CORE_CONSTANT.KPI_PIVOT_NULL_STRING, ''),
      bodyCells: extractDimensionBodyCells(sheetRows, columnIndex),
    })),
    ...kpiColumns.map((column, kpiColumnIndex): DashboardCsvColumn => {
      const columnIndex = dimensionColumns.length + kpiColumnIndex

      return {
        type: 'kpi',
        title: column.title,
        blockLabels: column.blockLabels,
        actualBodyCells: extractKpiBodyCells(sheetRows, columnIndex, 'actual'),
        goalBodyCells: extractKpiBodyCells(sheetRows, columnIndex, 'goal'),
      }
    }),
  ]
}

const extractDimensionBodyCells = (sheetRows: SheetRow[], columnIndex: number): string[] =>
  sheetRows.flatMap((sheetRow) =>
    getCellsForColumn(sheetRow, columnIndex).map((cell) => {
      if (cell?.recordNode === undefined) {
        return ''
      }
      const fieldName = sheetRow.columns[columnIndex]?.uiColumn?.fieldName ?? ''

      return castString(cell.recordNode.attributes[`${fieldName}_label`]) ?? castString(cell.value) ?? ''
    }),
  )

const extractKpiBodyCells = (sheetRows: SheetRow[], columnIndex: number, role: 'actual' | 'goal'): string[] =>
  sheetRows.flatMap((sheetRow) =>
    getCellsForColumn(sheetRow, columnIndex).map((cell) => {
      if (cell?.recordNode === undefined) {
        return ''
      }
      const fieldName = sheetRow.columns[columnIndex]?.uiColumn?.fieldName ?? ''
      const values = extractActualAndGoal(fieldName, cell.recordNode)

      return `${roundValue(values[role]) ?? ''}`
    }),
  )

const getCellsForColumn = (
  sheetRow: SheetRow,
  columnIndex: number,
): Array<SheetRow['columns'][number]['innerCells'][number] | undefined> => {
  const cells = sheetRow.columns.at(columnIndex)?.innerCells ?? []
  const innerRowIndexes = sheetRow.height >= 1 ? Array.range(0, sheetRow.height - 1) : []

  return innerRowIndexes.map((innerRowIndex) => cells.findLast((cell) => cell.innerRowIndexStart <= innerRowIndex))
}
